import { AnimationDelay, NumberOfGuesses } from "./Consts";
import { isEnglish } from "./language";

export function getColors(solution, currAnswer) {
  let colors = Array(solution.length).fill("gray");
  const solutionChars = solution.map((char) => convertFromLastLetter(char));
  const currAnsChars = currAnswer.map((char) => convertFromLastLetter(char));
  // A credited solution letter is blanked out with null rather than a letter,
  // so that guessing that letter cannot match an already-used square.
  for (let i = 0; i < solutionChars.length; i++) {
    if (solutionChars[i] === " ") continue;
    if (currAnsChars[i] === solutionChars[i]) {
      colors[i] = "green";
      solutionChars[i] = null;
    }
  }

  for (let i = 0; i < solutionChars.length; i++) {
    if (solutionChars[i] === " " || colors[i] === "green") continue;
    if (!currAnsChars[i]) continue;
    const index = solutionChars.indexOf(currAnsChars[i]);
    if (index !== -1) {
      colors[i] = "orange";
      solutionChars[index] = null;
    }
  }
  return colors;
}

// The squares are laid out left-to-right for both languages, so a Hebrew answer
// is typed into them from the last index backwards and an English one from the
// first index forwards.
export function getNextSquare(currAnswer, language) {
  if (isEnglish(language)) {
    for (let i = 0; i < currAnswer.length; i++) {
      if (currAnswer[i] === "") return i;
    }
    return null;
  }
  for (let i = currAnswer.length - 1; i >= 0; i--) {
    if (currAnswer[i] === "") return i;
  }
  return null;
}

// The square backspace should clear: the last one that was filled in.
export function getPrevSquare(currAnswer, solution, language) {
  let nextSquare = getNextSquare(currAnswer, language);
  if (isEnglish(language)) {
    // Every square is full, so the last one typed is the rightmost.
    if (nextSquare == null) return currAnswer.length - 1;
    // Nothing has been typed yet.
    if (nextSquare === 0) return 0;
    nextSquare--;
    if (solution[nextSquare] === " ") return nextSquare - 1;
    return nextSquare;
  }
  if (nextSquare == null) return 0;
  if (nextSquare === currAnswer.length - 1) return currAnswer.length - 1;
  nextSquare++;
  if (solution[nextSquare] === " ") return nextSquare + 1;
  return nextSquare;
}

export function getEmptyAnswer(solution) {
  let answer = [];
  for (let j = 0; j < solution.length; j++) {
    if (solution[j] === " ") answer.push(" ");
    else answer.push("");
  }
  return answer;
}

export function calcStyles(guesses, solution) {
  const allStyles = getDefaultStyles(solution.length);
  for (let i = 0; i < guesses.length; i++) {
    const currStyle = allStyles[i];
    const colors = getColors(solution, guesses[i]);
    for (let j = 0; j < solution.length; j++)
      currStyle[j] = { backgroundColor: colors[j] };
  }
  return allStyles;
}

export function getDefaultStyles(nSquares) {
  const styles = [];
  for (let i = 0; i < NumberOfGuesses; i++) {
    const rowStyles = [];
    for (let j = 0; j < nSquares; j++) {
      rowStyles.push({
        backgroundColor: "white",
      });
    }
    styles.push(rowStyles);
  }

  return styles;
}
export function arraysAreEqual(arr1, arr2) {
  return Boolean(
    arr1 &&
    arr2 &&
    arr1.length === arr2.length &&
    arr1.every((element, index) => element === arr2[index]),
  );
}

// The squares that end a word, where Hebrew takes a final-letter form. English
// has no such forms, so no square is ever a "last letter".
export const getLastLetterIndices = (solution, language) => {
  if (isEnglish(language)) return [];
  const lastLetterIndices = [];
  for (let i = solution.length; i >= 0; i--) {
    if (i === 0 || solution[i - 1] === " ") lastLetterIndices.push(i);
  }
  return lastLetterIndices;
};

export const getStringLengths = (arr) => {
  let lengths = [];
  let currentLength = 0;

  for (let char of arr) {
    if (char === " ") {
      if (currentLength > 0) {
        lengths.push(currentLength);
        currentLength = 0;
      }
    } else {
      currentLength++;
    }
  }

  if (currentLength > 0) {
    lengths.push(currentLength);
  }

  return `(${lengths.join(",")})`;
};
export const getUrl = () => {
  const useLocalServer = false;
  if (
    ["10.0.0.9", "localhost"].includes(window.location.hostname) &&
    useLocalServer
  ) {
    console.log("usingLocalServer");
    return "http://10.0.0.9:5000/";
  }
  return process.env.REACT_APP_URL;
};
export const isValidLetter = (value, isLastLetter, language) => {
  if (isEnglish(language)) return /^[a-zA-Z]$/.test(value);
  return (
    "אבגדהוזחטיכלמנסעפצקרשת".indexOf(value) !== -1 ||
    ("םןףךץ".indexOf(value) !== -1 && isLastLetter)
  );
};

export const convertToLastLetter = (val) => {
  switch (val) {
    case "צ":
      return "ץ";
    case "פ":
      return "ף";
    case "מ":
      return "ם";
    case "נ":
      return "ן";
    case "כ":
      return "ך";
    default:
      return val;
  }
};

export const convertFromLastLetter = (val) => {
  switch (val) {
    case "ץ":
      return "צ";
    case "ף":
      return "פ";
    case "ם":
      return "מ";
    case "ן":
      return "נ";
    case "ך":
      return "כ";
    default:
      return val;
  }
};

export const getMaxDelay = (solution, isMultiRiddle) => {
  const n = solution.length;
  // Max stagger delay: first square starts at AnimationDelay * n (e.g., 80ms * 5 = 400ms for 5-letter word)
  // Total time = max stagger + animation duration
  const animationDuration = 900; // Duration of flip animation in milliseconds (0.9s)
  const maxDelay = AnimationDelay * n + animationDuration;
  return maxDelay;
};

export const getHMSFormat = (timeInSeconds_) => {
  if (!isNumeric(timeInSeconds_)) return "00:00";

  const timeInSeconds = Math.round(timeInSeconds_);
  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor((timeInSeconds % 3600) / 60);
  const seconds = timeInSeconds % 60;
  const formattedTime =
    hours > 0
      ? `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
          2,
          "0",
        )}:${String(seconds).padStart(2, "0")}`
      : `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
          2,
          "0",
        )}`;
  return formattedTime;
};
export const isNumeric = (str) => {
  return (
    !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str))
  ); // ...and ensure strings of whitespace fail
};
